import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ChevronLeft, ArrowRight, CheckCircle } from 'lucide-react';
import SiteHeaderV2 from '@/components/website/SiteHeaderV2';
import SiteFooterV2 from '@/components/website/SiteFooterV2';
import PageHeaderV2 from '@/components/website/PageHeaderV2';

const ExercisesInfoPage = () => {
  const navigate = useNavigate();

  const exercises = [
    {
      name: "Petites Pauses (Buteyko)",
      duration: "2s - 3s - 5-10s",
      description: "Technique fondamentale pour améliorer la tolérance au CO2. Inspiration légère, expiration douce, puis pause après l'expiration.",
      benefits: ["Réduit l'hyperventilation", "Améliore l'oxygénation tissulaire", "Calme le système nerveux"],
      color: "from-sky-500 to-sky-600"
    },
    {
      name: "Respiration Cohérente",
      duration: "5s - 5s",
      description: "Respiration à 6 cycles par minute pour synchroniser le cœur et le cerveau et maximiser la variabilité cardiaque.",
      benefits: ["Équilibre le système nerveux", "Réduit le stress", "Améliore la récupération"],
      color: "from-sky-500 to-sky-600"
    },
    {
      name: "Respiration Carrée",
      duration: "4s - 4s - 4s - 4s",
      description: "Technique structurée avec quatre temps égaux pour apprendre à contrôler sa respiration.",
      benefits: ["Améliore la concentration", "Gère l'anxiété", "Renforce le contrôle respiratoire"],
      color: "from-violet-500 to-purple-500"
    },
    {
      name: "Technique 4-7-8",
      duration: "4s - 7s - 8s",
      description: "Technique relaxante avec une longue rétention et expiration. Idéale avant le sommeil.",
      benefits: ["Favorise l'endormissement", "Réduit l'anxiété", "Active la relaxation profonde"],
      color: "from-sky-500 to-sky-600"
    },
    {
      name: "Focus sur la capacité à expirer : Allongement du temps expiratoire",
      duration: "2s - 4s",
      description: "Expiration lente à travers les lèvres pincées pour ralentir le débit d'air.",
      benefits: ["Réduit l'essoufflement", "Calme la respiration rapide", "Améliore le contrôle expiratoire"],
      color: "from-sky-400 to-sky-500"
    },
    {
      name: "Respiration Calmante",
      duration: "3s - 6s",
      description: "Ratio 2:1 avec expiration deux fois plus longue que l'inspiration.",
      benefits: ["Activation parasympathique", "Réduction du rythme cardiaque", "Apaisement rapide"],
      color: "from-cyan-500 to-sky-500"
    }
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#FFFFFF", fontFamily: "Figtree, sans-serif" }}>
      <SiteHeaderV2 />
      
      {/* Page Header Image */}
      <PageHeaderV2 
        title="Exercices de Respiration" 
        subtitle="Programme complet de rééducation respiratoire"
      />
      
      {/* Hero */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <p className="text-xl text-slate-600 leading-relaxed">
            Découvrez notre programme complet d&apos;exercices de rééducation respiratoire, 
            conçus pour traiter l&apos;hyperventilation, la respiration dysfonctionnelle et les symptômes du COVID Long.
          </p>
        </div>
      </section>

      {/* Exercises */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="grid md:grid-cols-2 gap-8">
            {exercises.map((exercise, index) => (
              <Card key={index} className="border-0 shadow-lg overflow-hidden">
                <div className={`h-2 bg-gradient-to-r ${exercise.color}`} />
                <CardContent className="p-6">
                  <h3 className="text-xl font-medium mb-2">{exercise.name}</h3>
                  <p className="text-sky-600 font-mono text-sm mb-3">{exercise.duration}</p>
                  <p className="text-slate-600 mb-4">{exercise.description}</p>
                  <div className="space-y-2">
                    {exercise.benefits.map((benefit, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm text-slate-500">
                        <CheckCircle className="w-4 h-4 text-sky-500" />
                        {benefit}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-3xl sm:text-4xl mb-6" style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 300 }}>
            Prêt à Pratiquer ?
          </h2>
          <p className="text-slate-300 text-lg mb-8">
            Accédez à l&apos;application interactive avec animations guidées, sons de respiration et suivi de vos progrès.
          </p>
          <Button onClick={() => navigate('/app-landing')} className="px-10 py-6 text-lg rounded-full bg-cyan-500 hover:bg-cyan-600">
            Lancer l&apos;application
            <ArrowRight className="ml-2" size={20} />
          </Button>
        </div>
      </section>

      <SiteFooterV2 />
    </div>
  );
};

export default ExercisesInfoPage;
